import { amountToDisplayValue, NumberFormat } from '@billy/design-system'
import { BigNumber } from 'bignumber.js'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetchAccounts } from '@features/accounts/hooks/useFetchAccounts'
import { getImportedInvoiceLineNumber } from '@features/importedInvoices/utils/getImportedInvoiceLineNumber'
import { getImportedInvoiceVatRate } from '@features/importedInvoices/utils/getImportedInvoiceVatRate'
import { useTaxRates } from '@modules-deprecated/app/taxrates/taxRatesContext'

import { getCurrentLocale } from '../../../../../../../../utils/getCurrentLocale'
import { ImportedInvoiceApprovedLine } from '../../../../../../types/importedInvoiceApproved'
import { ImportedInvoiceDetailAccordion } from '../ImportedInvoiceDetailAccordion'
import { ImportedInvoiceDetailRow, ImportedInvoiceDetailRows } from '../ImportedInvoiceDetailRows'

interface ImportedInvoiceDetailLineProps {
  currencyId: string
  defaultOpen: boolean
  line: ImportedInvoiceApprovedLine
  lineIndex: number
}

/** One approved invoice line. Amounts are the stored ones, never recomputed from the VAT rate. */
export const ImportedInvoiceDetailLine = ({
  currencyId,
  defaultOpen,
  line,
  lineIndex,
}: ImportedInvoiceDetailLineProps): ReactElement => {
  const { t } = useTranslation()
  const { accounts } = useFetchAccounts()
  const { taxRatesIncludingDeactivated } = useTaxRates()

  const locale = getCurrentLocale()
  const categoryName = useMemo(
    () => accounts.find((account) => account.id === line.accountId)?.name,
    [accounts, line.accountId],
  )
  const vatRate = getImportedInvoiceVatRate(taxRatesIncludingDeactivated, line.taxRateId)
  const amountExclVat = Number(line.netAmount)
  const vatAmount = Number(line.taxAmount)
  const amountInclVat = new BigNumber(line.netAmount).plus(line.taxAmount).toNumber()

  const formattedVatAmount = amountToDisplayValue(vatAmount, false, currencyId)
  const formattedAmountInclVat = amountToDisplayValue(amountInclVat, false, currencyId)
  const vatValue =
    vatRate === null ? formattedVatAmount : `${NumberFormat.getPercentNumber(vatRate, locale)} • ${formattedVatAmount}`
  const title =
    categoryName ?? t('imported_invoices.review.form.line', { number: getImportedInvoiceLineNumber(lineIndex) })

  return (
    <ImportedInvoiceDetailAccordion defaultOpen={defaultOpen} subtitle={formattedAmountInclVat} title={title}>
      <ImportedInvoiceDetailRows>
        <ImportedInvoiceDetailRow
          label={t('imported_invoices.review.form.description')}
          value={line.description || '-'}
        />
        <ImportedInvoiceDetailRow
          label={t('imported_invoices.review.form.amount_excl_vat')}
          value={amountToDisplayValue(amountExclVat, false, currencyId)}
        />
        <ImportedInvoiceDetailRow label={t('imported_invoices.detail.vat')} value={vatValue} />
        <ImportedInvoiceDetailRow
          label={t('imported_invoices.review.form.amount_incl_vat')}
          value={formattedAmountInclVat}
        />
      </ImportedInvoiceDetailRows>
    </ImportedInvoiceDetailAccordion>
  )
}
